// Quiz-local duplicate of app/(marketing)/_components/heroImages.ts's
// shuffleHeroImages. Deliberately not shared with the marketing hero
// carousel — keeping this separate means changes here (or there) carry zero
// risk of affecting the other already-shipped carousel.

/**
 * Returns a new array containing a random permutation of `items` (Fisher–
 * Yates, using the injected `rng` so callers can make it deterministic in
 * tests). When `previousLast` is given and the shuffled result's first
 * element has that `src`, one swap is performed against a different index so
 * the same image is never shown twice in a row across a reshuffle boundary.
 *
 * `rng` must return a value in `[0, 1)` — matching `Math.random()`'s
 * contract. A value of exactly `1` will index out of bounds.
 *
 * `items.length < 2` is a degenerate case — the no-repeat constraint can't
 * be satisfied (or is meaningless), so the input is returned as a new array
 * with no swap attempted.
 */
export function shuffleImagePool<T extends { src: string }>(
  items: readonly T[],
  rng: () => number = Math.random,
  previousLast?: string,
): T[] {
  const result = [...items];

  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [result[i], result[j]] = [result[j]!, result[i]!];
  }

  if (result.length >= 2 && previousLast && result[0]!.src === previousLast) {
    // Swap slot 0 with some other slot. Any of them is safe: the only
    // forbidden arrangement is index 0 === previousLast, and swapping moves
    // that src away from index 0 without re-introducing it elsewhere.
    const swapWith = 1 + Math.floor(rng() * (result.length - 1));
    [result[0], result[swapWith]] = [result[swapWith]!, result[0]!];
  }

  return result;
}
